/**
 * Status pill shown right-aligned in the card header.
 * Pill is read-only — status changes by backend transition, not user click.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

import { StatusPillTone } from './labels';

interface StatusPillProps {
  label: string;
  tone: StatusPillTone;
}

const TONE_COLOR_MAP: Record<
  StatusPillTone,
  { bgKey: string; fgKey: string; alpha: number }
> = {
  // `info` is the "pending action" tone (Confirm parameters, Confirm
  // plan, etc.). Uses theme.secondary (purple) — primary (blue) is
  // reserved for the active footer Confirm button so the read-only
  // badge doesn't compete visually with the actionable control.
  info: { bgKey: 'secondary.main', fgKey: 'secondary.main', alpha: 0.12 },
  success: { bgKey: 'success.main', fgKey: 'success.dark', alpha: 0.14 },
  warning: { bgKey: 'warning.main', fgKey: 'warning.dark', alpha: 0.16 },
  neutral: { bgKey: 'text.secondary', fgKey: 'text.secondary', alpha: 0.1 },
};

export const StatusPill: React.FC<StatusPillProps> = ({ label, tone }) => {
  if (!label) return null;
  const spec = TONE_COLOR_MAP[tone];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        px: 1,
        borderRadius: '11px',
        bgcolor: (theme) => {
          // Resolve the dotted token (e.g., 'primary.main')
          // to the actual color, then apply alpha.
          const [palette, shade] = spec.bgKey.split('.');
          const root = (theme.palette as unknown as Record<string, unknown>)[
            palette
          ] as Record<string, string> | undefined;
          const c = root?.[shade] ?? '#999';
          // Convert hex to rgba with alpha.
          return hexToRgba(c, spec.alpha);
        },
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '0.02em',
          color: spec.fgKey,
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

function hexToRgba(hex: string, alpha: number): string {
  if (hex.startsWith('rgb')) {
    // Already rgb/rgba — replace alpha if present, else inject.
    if (hex.startsWith('rgba(')) {
      return hex.replace(/[\d.]+\)$/, `${alpha})`);
    }
    return hex.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
  }
  const m = hex.replace('#', '');
  if (m.length !== 6 && m.length !== 3) return `rgba(153, 153, 153, ${alpha})`;
  const expanded =
    m.length === 3
      ? m
          .split('')
          .map((c) => c + c)
          .join('')
      : m;
  const r = parseInt(expanded.substring(0, 2), 16);
  const g = parseInt(expanded.substring(2, 4), 16);
  const b = parseInt(expanded.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
